const INVALID_FILENAME_CHARS = /[<>:"/\\|?*]/g;

export function sanitizeFilenamePart(value) {
  return String(value ?? "")
    .trim()
    .replace(INVALID_FILENAME_CHARS, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function isMissingDocumentNumber(value) {
  const normalized = String(value ?? "").trim();
  return !normalized || normalized === "—" || normalized === "-";
}

function buildPayrollPeriodPart(startDate, endDate) {
  const start = String(startDate ?? "").trim();
  const end = String(endDate ?? "").trim();

  if (!start && !end) {
    return "All_Time";
  }

  if (start && end) {
    return start === end ? start : `${start}_to_${end}`;
  }

  return start || end;
}

function resolvePayrollEmployeePrefix(reportModel) {
  if (reportModel?.isGrouped) {
    const groupedName = reportModel?.header?.employeeName;
    if (groupedName && /\d+\s+Employees/i.test(groupedName)) {
      return "My_Crew";
    }
  }

  const scope = reportModel?.header?.employeeScope;

  if (scope === "MY_CREW" || scope === "ALL_EMPLOYEES") {
    return "My_Crew";
  }

  if (scope && scope !== "MY_TIME") {
    const scopedName = sanitizeFilenamePart(reportModel?.header?.employeeName);
    if (scopedName) {
      return scopedName;
    }
  }

  const headerName = reportModel?.header?.employeeName;
  if (headerName && /\d+\s+Employees/i.test(headerName)) {
    return "My_Crew";
  }

  const detailRows = reportModel?.detail?.detailRows || [];
  if (detailRows.length > 0) {
    const names = new Set(
      detailRows.map((row) => row.name).filter((name) => name && name !== "Employee"),
    );

    if (names.size > 1) {
      return "My_Crew";
    }

    if (names.size === 1) {
      return sanitizeFilenamePart([...names][0]);
    }
  }

  const summaryRows = reportModel?.detail?.summaryRows || [];
  if (summaryRows.length > 1) {
    return "My_Crew";
  }

  if (summaryRows.length === 1) {
    return sanitizeFilenamePart(summaryRows[0].label);
  }

  if (headerName) {
    const normalized = headerName.trim();
    if (/^my crew$/i.test(normalized)) {
      return "My_Crew";
    }

    if (
      normalized &&
      normalized !== "—" &&
      normalized !== "Unknown Employee"
    ) {
      return sanitizeFilenamePart(normalized);
    }
  }

  return null;
}

export function buildEstimatePdfFilename(reportModel) {
  const number = reportModel?.header?.estimateNumber;
  const id = reportModel?.header?.estimateId;

  if (!isMissingDocumentNumber(number)) {
    return `${sanitizeFilenamePart(number)}.pdf`;
  }

  const fallback = id != null ? String(id) : "report";
  return `${sanitizeFilenamePart(fallback)}.pdf`;
}

export function buildInvoicePdfFilename(reportModel) {
  const number = reportModel?.header?.invoiceNumber;
  const id = reportModel?.header?.invoiceId;

  if (!isMissingDocumentNumber(number)) {
    return `${sanitizeFilenamePart(number)}.pdf`;
  }

  const fallback = id != null ? String(id) : "report";
  return `${sanitizeFilenamePart(fallback)}.pdf`;
}

export function buildPayrollPdfFilename(reportModel) {
  const periodPart = buildPayrollPeriodPart(
    reportModel?.header?.startDate,
    reportModel?.header?.endDate,
  );
  const employeePrefix = resolvePayrollEmployeePrefix(reportModel);

  const parts = ["Payroll"];
  if (employeePrefix) {
    parts.push(employeePrefix);
  }
  parts.push(periodPart);

  return `${parts.join("_")}.pdf`;
}
